import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

interface WindowPortalProps {
  children: React.ReactNode;
  targetWindow: Window | null;
}

const WindowPortal: React.FC<WindowPortalProps> = ({ children, targetWindow }) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!targetWindow) return;

    // Inject a firm style block to force strict bounds without relying on vh (which can bug out in PiP)
    const styleId = 'pip-portal-styles';
    let style = targetWindow.document.getElementById(styleId) as HTMLStyleElement;
    if (!style) {
      style = targetWindow.document.createElement('style');
      style.id = styleId;
      targetWindow.document.head.appendChild(style);
    }
    style.innerHTML = `
      * { box-sizing: border-box; }
      html, body { margin: 0 !important; padding: 0 !important; background-color: white !important; overflow: hidden !important; position: fixed !important; inset: 0 !important; }
      #pip-root { position: fixed !important; inset: 0 !important; display: flex !important; flex-direction: column !important; overflow: hidden !important; background-color: white !important; }
      
      /* Custom Scrollbar to bypass Chrome's PiP edge-resize overlay */
      *::-webkit-scrollbar { width: 10px !important; height: 10px !important; }
      *::-webkit-scrollbar-track { background: transparent !important; }
      *::-webkit-scrollbar-thumb { 
        background-color: #cbd5e1 !important; 
        border-radius: 5px !important; 
        border: 2px solid transparent !important;
        background-clip: padding-box !important;
      }
      *::-webkit-scrollbar-thumb:hover { 
        background-color: #94a3b8 !important; 
      }
      * { scrollbar-width: thin !important; scrollbar-color: #cbd5e1 transparent !important; }
    `;

    // Create a container div inside the target window's body
    let div = targetWindow.document.getElementById('pip-root') as HTMLDivElement;
    if (!div) {
      div = targetWindow.document.createElement('div');
      div.id = 'pip-root';
      targetWindow.document.body.appendChild(div);
    }
    setContainer(div);

    return () => {
      if (div.parentNode) {
        div.parentNode.removeChild(div);
      }
    };
  }, [targetWindow]);

  if (!container) return null;

  return ReactDOM.createPortal(children, container);
};

export default WindowPortal;
